import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileContract, FaShoppingCart, FaMoneyBillWave, FaTruck, FaAward, FaBalanceScale } from 'react-icons/fa';
import './Terms.css';

const Terms = () => {
  const { t } = useTranslation();
  const currentDate = new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="terms-page">
      {/* Header Section */}
      <section className="terms-header">
        <div className="terms-header-overlay"></div>
        <div className="container">
          <h1><FaFileContract /> {t('terms.title')}</h1>
          <p>{t('terms.subtitle')}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="terms-content">
        <div className="terms-container">
          <p className="terms-last-updated">
            {t('terms.lastUpdated')}: {currentDate}
          </p>

          {/* Introduction */}
          <div className="terms-section">
            <h2>{t('terms.intro.title')}</h2>
            <p>{t('terms.intro.content')}</p>
          </div>

          {/* Scope */}
          <div className="terms-section">
            <h2>{t('terms.scope.title')}</h2>
            <p>{t('terms.scope.content')}</p>
            <ul>
              {t('terms.scope.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="terms-section">
            <h2><FaShoppingCart /> {t('terms.services.title')}</h2>
            <p>{t('terms.services.description')}</p>
            <ul>
              {t('terms.services.offerings', { returnObjects: true }).map((offering, index) => (
                <li key={index}>{offering}</li>
              ))}
            </ul>
            <p><em>{t('terms.services.availability')}</em></p>
          </div>

          {/* Registration */}
          <div className="terms-section">
            <h2>{t('terms.registration.title')}</h2>
            
            <h3>{t('terms.registration.requirements.title')}</h3>
            <p>{t('terms.registration.requirements.content')}</p>
            <ul>
              {t('terms.registration.requirements.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.registration.obligations.title')}</h3>
            <ul>
              {t('terms.registration.obligations.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Ordering Process */}
          <div className="terms-section">
            <h2>{t('terms.ordering.title')}</h2>
            
            <h3>{t('terms.ordering.process.title')}</h3>
            <p>{t('terms.ordering.process.content')}</p>
            <ul>
              {t('terms.ordering.process.methods', { returnObjects: true }).map((method, index) => (
                <li key={index}>{method}</li>
              ))}
            </ul>

            <h3>{t('terms.ordering.confirmation.title')}</h3>
            <p>{t('terms.ordering.confirmation.content')}</p>

            <div className="terms-info-box">
              <strong>{t('terms.ordering.minimum.title')}</strong>
              <p>{t('terms.ordering.minimum.content')}</p>
            </div>
          </div>

          {/* Pricing and Payment */}
          <div className="terms-section">
            <h2><FaMoneyBillWave /> {t('terms.pricing.title')}</h2>
            
            <h3>{t('terms.pricing.prices.title')}</h3>
            <ul>
              {t('terms.pricing.prices.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.pricing.payment.title')}</h3>
            <p><strong>{t('terms.pricing.payment.methods')}</strong></p>
            <ul>
              {t('terms.pricing.payment.options', { returnObjects: true }).map((option, index) => (
                <li key={index}>{option}</li>
              ))}
            </ul>
            <p><em>{t('terms.pricing.payment.terms')}</em></p>

            <div className="terms-warning-box">
              <strong>{t('terms.pricing.default.title')}</strong>
              <p>{t('terms.pricing.default.content')}</p>
            </div>
          </div>

          {/* Delivery */}
          <div className="terms-section">
            <h2><FaTruck /> {t('terms.delivery.title')}</h2>
            
            <h3>{t('terms.delivery.terms.title')}</h3>
            <ul>
              {t('terms.delivery.terms.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.delivery.shipping.title')}</h3>
            <p>{t('terms.delivery.shipping.content')}</p>

            <h3>{t('terms.delivery.acceptance.title')}</h3>
            <p>{t('terms.delivery.acceptance.content')}</p>
          </div>

          {/* Quality */}
          <div className="terms-section">
            <h2><FaAward /> {t('terms.quality.title')}</h2>
            
            <h3>{t('terms.quality.assurance.title')}</h3>
            <p>{t('terms.quality.assurance.content')}</p>
            <ul>
              {t('terms.quality.assurance.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.quality.defects.title')}</h3>
            <p>{t('terms.quality.defects.content')}</p>
          </div>

          {/* Returns */}
          <div className="terms-section">
            <h2>{t('terms.returns.title')}</h2>
            
            <h3>{t('terms.returns.policy.title')}</h3>
            <p>{t('terms.returns.policy.content')}</p>
            <ul>
              {t('terms.returns.policy.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.returns.cancellation.title')}</h3>
            <p>{t('terms.returns.cancellation.content')}</p>
          </div>

          {/* Liability */}
          <div className="terms-section">
            <h2><FaBalanceScale /> {t('terms.liability.title')}</h2>
            
            <h3>{t('terms.liability.limitation.title')}</h3>
            <p>{t('terms.liability.limitation.content')}</p>
            <ul>
              {t('terms.liability.limitation.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3>{t('terms.liability.force.title')}</h3>
            <p>{t('terms.liability.force.content')}</p>
          </div>

          {/* Data Protection */}
          <div className="terms-section">
            <h2>{t('terms.dataProtection.title')}</h2>
            <p>{t('terms.dataProtection.content')}</p>
          </div>

          {/* Termination */}
          <div className="terms-section">
            <h2>{t('terms.termination.title')}</h2>
            
            <h3>{t('terms.termination.duration.title')}</h3>
            <p>{t('terms.termination.duration.content')}</p>
            <p><strong>{t('terms.termination.duration.grounds')}</strong></p>
            <ul>
              {t('terms.termination.duration.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Intellectual Property */}
          <div className="terms-section">
            <h2>{t('terms.intellectual.title')}</h2>
            <p>{t('terms.intellectual.content')}</p>
          </div>

          {/* Amendments */}
          <div className="terms-section">
            <h2>{t('terms.amendments.title')}</h2>
            <p>{t('terms.amendments.content')}</p>
          </div>

          {/* Severability */}
          <div className="terms-section">
            <h2>{t('terms.severability.title')}</h2>
            <p>{t('terms.severability.content')}</p>
          </div>

          {/* Jurisdiction */}
          <div className="terms-section">
            <h2>{t('terms.jurisdiction.title')}</h2>
            <p><strong>{t('terms.jurisdiction.law')}</strong></p>
            <p>{t('terms.jurisdiction.venue')}</p>
          </div>

          {/* Contact Box */}
          <div className="terms-contact-box">
            <h3>{t('terms.contact.title')}</h3>
            <p>{t('terms.contact.content')}</p>
            <p><strong>{t('terms.contact.company')}</strong></p>
            <p>{t('terms.contact.address')}</p>
            <p><strong>{t('terms.contact.phone')}</strong></p>
            <p><strong>{t('terms.contact.email')}</strong></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;

